import _ from 'lodash';
import {
  LOCAL_TITLE_CLASS,
  LOCAL_TITLE_SUFFIX
} from '../../_editor/_core/local-title-block/local-title-block.component';
import { UploadItemData } from '../../_editor/upload/upload.types';
import { determineLocalTitle } from '../../_editor/_core/local-title-block/local-title-block.utils';
import { sanitizeFilenameComponent } from '../../../lib/utils';

const getLocalTitleParents = (acc: HTMLElement[], fileElement?: HTMLElement | null) => {
  // Find the local title that belongs to this ce-block, not nested blocks or file containers
  const localTitles = fileElement?.querySelectorAll('.' + LOCAL_TITLE_CLASS);
  const parent = localTitles ? Array.from(localTitles).find(title => {
    // Exclude titles inside file containers
    if (title.closest('.file-container')) return false;
    // Only include titles whose closest ce-block is this fileElement
    return title.closest('.ce-block') === fileElement;
  }) as HTMLElement | undefined : undefined;

  // Only include parent if it has actual local title content (text ending with ")")
  if (parent && parent.textContent?.endsWith(LOCAL_TITLE_SUFFIX)) {
    // Add grandparents local titles to the accumulator first
    getLocalTitleParents(acc, fileElement?.parentElement?.closest('.' + 'ce-block') as HTMLElement | null);
    acc.push(parent);
  }

  return acc;
};

// TODO: Need to deprecate this in favour of non-context based file name gathering
export const getDownloadFileName = ({
  baseActiveSection,
  itemData,
  getLatestFileLabel,
  skipFileElementCheck,
  tag,
  useOriginalFileName
}: {
  baseActiveSection?: number | null;
  itemData: UploadItemData;
  getLatestFileLabel?: (_: string) => string | null;
  skipFileElementCheck?: boolean;
  tag?: string;
  useOriginalFileName?: boolean;
}) => {
  let fileName = '';

  if (!skipFileElementCheck) {
    // Get prefix of all nested local title blocks values
    const fileContainer = document.getElementById('file-' + itemData.fileContainerId);
    const fileElement = fileContainer?.closest('.' + 'ce-block');
    if (fileElement) {
      const parentTitles = [
        ...getLocalTitleParents([], fileElement as HTMLElement),
        fileContainer?.querySelector('.' + LOCAL_TITLE_CLASS) as HTMLElement
      ].map((e) => (e.textContent?.endsWith(LOCAL_TITLE_SUFFIX) ? e.textContent?.split(LOCAL_TITLE_SUFFIX)?.[0] : ''));

      // Collect all nested local titles and prepend to file name
      const sectionLetter = typeof baseActiveSection === 'number' ? determineLocalTitle(1, baseActiveSection) : '';
      const parentTitle = sectionLetter + parentTitles.join('');

      if (parentTitle) {
        // Add period after section local title if file is nested further than that. This should apply in all cases, but we add a default behavior just in case.
        if (parentTitle.length > 1) fileName += `${parentTitle.charAt(0)}.${parentTitle.slice(1)} - `;
        else fileName += `${parentTitle} - `;
      }
    }
  }

  // Add tag after section number if present (with defensive sanitization)
  if (tag && tag.trim()) {
    fileName += `${sanitizeFilenameComponent(tag)} - `;
  }

  // Get correct filename
  if (useOriginalFileName) {
    fileName += itemData.fileName;
  } else {
    fileName += getLatestFileLabel
      ? getLatestFileLabel(itemData.fileContainerId) || itemData.fileName
      : itemData.label || itemData.fileName;

    const fileExt = itemData.fileName.split('.').pop();
    if (fileExt && fileName) {
      // Only add file extension if it isn't already present at the end of the file, prevents double extensions when original fileName is used instead of file label
      const fileExtSuffix = '.' + fileExt;
      if (!fileName.endsWith(fileExtSuffix)) fileName += fileExtSuffix;
    }
  }

  // Ensure file name is unescaped
  const finalFileName = _.unescape(fileName);

  return finalFileName;
};
